import { useState } from 'react';
import { LogEntry } from '../types';
import { AlertCircle, Eye } from 'lucide-react';
import BodyModal from './BodyModal';
import { isDashboardExcludedUrl } from '../utils/dashboardFilters';

interface ErrorLogsProps {
  errors: LogEntry[];
}

export default function ErrorLogs({ errors }: ErrorLogsProps) {
  const [selectedBody, setSelectedBody] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getStatusColor = (statusCode: number) => {
    if (statusCode >= 500) return 'bg-red-100 text-red-800';
    if (statusCode >= 400) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const handleViewBody = (body?: string) => {
    setSelectedBody(body);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBody(undefined);
  };

  const filteredErrors = (errors ?? []).filter((e) => !isDashboardExcludedUrl(e.url));

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">
            Erros Recentes
            {filteredErrors.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">({filteredErrors.length})</span>
            )}
          </h3>
        </div>
        {filteredErrors.length === 0 ? (
          <div className="py-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            <p className="text-sm">Nenhum erro no período.</p>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Método
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  URL
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  IP
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tempo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Início
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Fim
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Body
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredErrors.map((error, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${getStatusColor(error.statusCode ?? 0)}`}>
                      {error.statusCode ?? '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {error.method}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                    {error.url}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {error.ip}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {error.responseTime != null ? `${error.responseTime}ms` : '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {error.responseTime != null
                      ? new Date(new Date(error.timestamp).getTime() - error.responseTime).toLocaleString('pt-BR')
                      : '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {new Date(error.timestamp).toLocaleString('pt-BR')}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {error.body ? (
                      <button
                        onClick={() => handleViewBody(error.body)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                        title="Ver body da requisição"
                      >
                        <Eye className="h-4 w-4" />
                        <span>Ver</span>
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
      <BodyModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        body={selectedBody}
        title="Request Body - Erro"
      />
    </>
  );
}

